import { apiGet, apiPost } from "./client";
import type {
  UserProgress,
  BadgeDefinition,
  XPEntry,
  StreaksResponse,
  FreezeStatus,
} from "@shared/types/gamification";

export interface BadgeWithStatus extends BadgeDefinition {
  earned: boolean;
  earned_at: string | null;
}

export interface TrialStatus {
  isActive: boolean;
  daysRemaining: number;
  startedAt: string | null;
  endsAt: string | null;
  hasExpired: boolean;
}

export interface TrialProgress {
  missionsCompleted: number;
  spEarned: number;
  xpEarned: number;
  badgesEarned: number;
  trialStartedAt: string | null;
}

export interface PremiumPromptData {
  variant: 1 | 2 | 3 | 4 | 5 | 6;
  title: string;
  message: string;
  stats: {
    missionsCompleted: number;
    spEarned: number;
    xpEarned: number;
    badgesEarned: number;
    academyModulesUnlocked: number;
  };
  show_premium_price: number;
  show_title: string;
}

export interface RefundEligibility {
  eligible: boolean;
  reason: string;
  missionCompletionPct: number;
  missionsCompleted: number;
  missionsAssigned: number;
  revenueGain: number;
  upgradeCost: number;
  isPremium: boolean;
  isSeasonComplete: boolean;
  showTitle: string;
  premiumActivatedAt: string | null;
  daysRemaining: number;
}

export const gamificationApi = {
  getProgress(): Promise<UserProgress> {
    return apiGet("/api/gamification/progress");
  },

  checkIn(): Promise<{
    streak: UserProgress["streak"];
    xpAwarded: number;
  }> {
    return apiPost("/api/gamification/checkin", {});
  },

  getBadges(): Promise<BadgeWithStatus[]> {
    return apiGet("/api/gamification/badges");
  },

  getBadgeCatalog(): Promise<BadgeWithStatus[]> {
    return apiGet("/api/gamification/badges");
  },

  getXPHistory(limit: number = 20): Promise<XPEntry[]> {
    return apiGet(`/api/gamification/xp-history?limit=${limit}`);
  },

  // Streak endpoints
  getStreaks(): Promise<StreaksResponse> {
    return apiGet("/api/streaks");
  },

  getFreezeStatus(): Promise<FreezeStatus> {
    return apiGet("/api/streaks/freezes");
  },

  // Trial endpoints
  startTrial(showId: number): Promise<{ started: boolean; endsAt: string }> {
    return apiPost("/api/trial/start", { showId });
  },

  getTrialStatus(showId: number): Promise<TrialStatus> {
    return apiGet(`/api/trial/status/${showId}`);
  },

  getTrialProgress(showId: number): Promise<TrialProgress> {
    return apiGet(`/api/trial/progress/${showId}`);
  },

  // Premium prompt
  getPremiumPrompt(showId: number): Promise<PremiumPromptData | null> {
    return apiGet(`/api/premium-prompt/${showId}`);
  },

  // Refund
  getRefundEligibility(showId: number): Promise<RefundEligibility> {
    return apiGet(`/api/refund/eligibility/${showId}`);
  },

  processRefund(showId: number): Promise<{ processed: boolean; message: string }> {
    return apiPost(`/api/refund/process/${showId}`, {});
  },
};
